import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { alpha, Box, Button, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import { Mail, Work } from '@mui/icons-material';
import { useQueryClient } from 'react-query';

import { NotificationContext, NotificationType } from '@bandido/shared/types';
import { NotificationResponseDto, PagedResponse } from '@bandido/shared/dtos';

import { getTimeSinceDate } from '../../helper';
import { useGetNotifications, useMarkAllNotificationsAsRead } from '../../queries';
import { useNavigate } from 'react-router-dom';
import { routes } from '@frontend/router/routes';
import { client } from '../../services';
import { QueryKeys } from '../../types';

export const notificationTypeIcon = {
  [NotificationType.CASE_STATUS_MODIFIED]: <Work />,
  [NotificationType.CASE_NOTE_ADDED]: <Work />,
  [NotificationType.CASE_CLIENT_REPLIED]: <Mail />,
  [NotificationType.CASE_EMAIL_ADDED]: <Mail />,
  [NotificationType.CASE_UNASSIGNED_ME]: <Work />,
  [NotificationType.CASE_ASSIGNED_TO_ME]: <Work />,
};
export interface INotification {
  id: string;
  notificationType: NotificationType;
  payload: {
    details?: Record<string, any>;
  };
  context: NotificationContext;
  user?: string | null;
  isRead: boolean;
  createdDate: string;
}

interface Props {
  open?: boolean;
  notificationIconRef: React.MutableRefObject<HTMLDivElement | null>
  onClose: () => void;
}

export const NotificationsDrawer = ({ open, onClose, notificationIconRef }: Props) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate =  useNavigate();
  const queryClient = useQueryClient();

  const { data } = useGetNotifications({
    currentPage: 0,
    pageSize: 20,
    search: '',
  });
  const notificationsQueryKey = [QueryKeys.getNotifications, 0, 20, ''];
  const unreadCount = data?.items?.filter((notification) => !notification.isRead).length ?? 0;

  const markAllAsReadMutation = useMarkAllNotificationsAsRead({
    onSuccess: () => {
      queryClient.setQueryData(
        notificationsQueryKey,
        (oldData?: PagedResponse<NotificationResponseDto>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((notification) => ({
            ...notification,
            isRead: true,
          })),
        };
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getNotifications] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getMyUserData] });
    },
  });

  const handleClickOutside = (event: MouseEvent) => {
    event.preventDefault();
    const existeIconRef = !(notificationIconRef.current && !notificationIconRef.current.contains(event.target as Node));
    const existeDrawer = !(drawerRef.current && !drawerRef.current.contains(event.target as Node));

    if (!existeDrawer && !existeIconRef) {
      onClose();
    }
  };

  const handleMarkAsRead = async (notifcationId: string) => {
    try {
      await client.post(`notifications/${notifcationId}/read`);
    } catch(e) {
      // Aca no uso el snackbar adrede, no me peleen en el PR
      console.error(e)
    }
    
  }

  const handleMarkAllAsRead = () => {
    if (markAllAsReadMutation.isLoading || unreadCount === 0) {
      return;
    }
    markAllAsReadMutation.mutate();
  };

  // TODO: esto va a ir modificando pq vamos a tener status no relacionados a casos
  const  handleClickNotification =  (notifcationId: string ,caseId: string) => ( )=> {
    try {
      handleMarkAsRead(notifcationId);
      navigate(routes.dashboard.caseView.replace(':id', caseId));
    } catch (error) {
      // Aca no uso el snackbar adrede, no me peleen en el PR
      console.error(error);
    }
   onClose();
  }

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <Drawer
      ref={drawerRef}
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      sx={{
        width: 390,
        '& .MuiDrawer-paper': {
          width: 390,
          minWidth: 390,
          height: 'calc(100vh - 64px)', // Asegura que el Drawer ocupe toda la altura
          zIndex: 1100,
          marginTop: '64px'
        },
      }}
    >
      <Box height={48} paddingX={2} paddingY={1}>
        <Typography variant="h6">{t('notifications.title')}</Typography>
      </Box>
      <Box display="flex" flexDirection="row-reverse">
        <Button
          color="primary"
          onClick={handleMarkAllAsRead}
          disabled={markAllAsReadMutation.isLoading || unreadCount === 0}
        >
          {t('notifications.clean')}
        </Button>
      </Box>
      <Box paddingX={2}>
        <Typography variant="caption">{t('notifications.recents')}</Typography>
      </Box>
      <List>
        <Divider />
        {data?.items.map((notification) => (
          <Fragment key={notification.id}>
            <ListItemButton 
            selected={!notification.isRead} 
            onClick={handleClickNotification(notification.id, notification.payload?.caseId)}
            >
              <ListItemIcon>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette[notification.context].light, 0.2),
                    border: `2px solid ${theme.palette[notification.context].dark}`,
                    color: theme.palette[notification.context].dark,
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                  }}
                >
                  {notificationTypeIcon[notification.notificationType]}
                </div>
              </ListItemIcon>
              <Box>
                <Box display="flex">
                  <Typography variant="subtitle1" fontWeight={500} color={theme.palette[notification.context].dark}> {t(`notifications.${notification.notificationType}.title`)}</Typography>
                </Box>
                <ListItemText
                  sx={{
                    '& b': {
                      fontWeight: '500',
                      fontSize: 'inherit',
                    },
                  }}
                  primary={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t(`notifications.${notification.notificationType}.message`, notification.payload),
                      }}
                    />
                  }
                  secondary={getTimeSinceDate(notification.createdAt)}
                />
              </Box>
            </ListItemButton>
            <Divider />
          </Fragment>
        ))}
      </List>
    </Drawer>
  );
};
